import { CalendarDate } from '@internationalized/date';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

type CalendarDateLike = {
  toString: () => string;
};

interface QuarterPickerProps {
  value?: CalendarDate | null;
  onChange?: (value: CalendarDate | null) => void;
  minValue?: CalendarDateLike | null;
  maxValue?: CalendarDateLike | null;
  isDisabled?: boolean;
  placeholder?: string;
  className?: string;
}

const monthToQuarter = (month: number) => Math.ceil(month / 3);

const parseYearQuarter = (
  value?: CalendarDateLike | null
): { year: number; quarter: number } | null => {
  if (!value) return null;
  const [year, month] = value.toString().split('-').map(Number);
  return year && month ? { year, quarter: monthToQuarter(month) } : null;
};

// A single-quarter picker — same visual language as `MonthPicker`, used for
// the Quarterly tab where picking individual months doesn't match the
// underlying period granularity.
const QuarterPicker = ({
  value,
  onChange,
  minValue,
  maxValue,
  isDisabled,
  placeholder = 'Select quarter',
  className,
}: QuarterPickerProps) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const min = parseYearQuarter(minValue);
  const max = parseYearQuarter(maxValue);
  const selected = value
    ? { year: value.year, quarter: monthToQuarter(value.month) }
    : null;

  const [viewYear, setViewYear] = useState(
    selected?.year ?? max?.year ?? min?.year ?? new Date().getFullYear()
  );

  useEffect(() => {
    if (open) setViewYear(selected?.year ?? viewYear);
    // Only re-sync the visible year when the popover opens.
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickAway = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [open]);

  const isQuarterDisabled = (year: number, quarter: number) => {
    if (
      min &&
      (year < min.year || (year === min.year && quarter < min.quarter))
    )
      return true;
    if (
      max &&
      (year > max.year || (year === max.year && quarter > max.quarter))
    )
      return true;
    return false;
  };

  const canGoPrevYear = !min || viewYear > min.year;
  const canGoNextYear = !max || viewYear < max.year;

  const label = selected
    ? `Q${selected.quarter} ${selected.year}`
    : placeholder;

  const handleSelectQuarter = (quarter: number) => {
    onChange?.(new CalendarDate(viewYear, (quarter - 1) * 3 + 1, 1));
    setOpen(false);
  };

  return (
    <div className={clsx('relative', className)} ref={wrapperRef}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'group flex min-h-[46px] w-full items-center gap-3 rounded-lg border border-transparent bg-indigo-100 px-4 py-[14px] text-left shadow-xs transition-colors',
          isDisabled
            ? 'pointer-events-none opacity-60'
            : 'cursor-pointer hover:bg-indigo-500',
          open ? 'bg-indigo-100' : ''
        )}
      >
        <Calendar className="size-4 shrink-0 text-indigo-800 transition-colors group-hover:text-white" />
        <span className="min-w-0 flex-1 text-sm font-medium text-indigo-800 transition-colors group-hover:text-white">
          {label}
        </span>
        <ChevronDown className="size-4 shrink-0 text-indigo-800 transition-colors group-hover:text-white" />
      </button>

      {open && (
        <div className="absolute top-[calc(100%+8px)] left-0 z-50 w-[220px] overflow-hidden rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_16px_38px_rgba(15,23,42,0.12)]">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              disabled={!canGoPrevYear}
              onClick={() => setViewYear((y) => y - 1)}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-slate-800">
              {viewYear}
            </span>
            <button
              type="button"
              disabled={!canGoNextYear}
              onClick={() => setViewYear((y) => y + 1)}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {[1, 2, 3, 4].map((quarter) => {
              const disabled = isQuarterDisabled(viewYear, quarter);
              const isSelected =
                selected?.year === viewYear && selected?.quarter === quarter;
              return (
                <button
                  key={quarter}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSelectQuarter(quarter)}
                  className={clsx(
                    'rounded-[10px] py-2 text-sm font-medium transition-colors',
                    disabled
                      ? 'pointer-events-none text-gray-300'
                      : isSelected
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-700 hover:bg-indigo-50'
                  )}
                >
                  Q{quarter}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuarterPicker;
